import { inject, Injectable } from '@angular/core';
import { UserInitializationService } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { AppModulesType } from '../../_shared/enums/appModulesType.enum';
import { GuiUserContractorSettings, User } from '../../_shared/model';
import { Contractor } from '../../_shared/model/contractor.model';
import { GuiUserSettings } from '../../_shared/model/userGuiSettings/guiUserSettings.model';
import { ContractorGroup } from '../../_shared/model/utils/contractorGroup.model';
import { HelperService, MfToastService, RulesService, StorageService } from '../../_shared/services';
import { ActiveModulesService } from '../../_shared/services/activeModules.service';
import { HelperNotificationService } from '../../_shared/services/helper-notification.service';
import { MenuService } from '../../_shared/services/menu.service';
import { ClearState, SetActiveContractor, SetActiveModules, SetLoggedUser, SetUserSettings } from '../../_shared/state/base/base.actions';
import { ClearTreatmentState } from '../../_shared/state/treatment/treatment.actions';
import { WebsocketActions } from '../../_shared/state/websocket/websocket.actions';
import { CommonService } from '../../theme/pages/home/_services';
import { ConfigGuiService } from '../../theme/pages/home/_services/config-gui.service';
import { ContractorSettingsApplicationService } from '../../theme/pages/home/_services/contractor-settings-application.service';
import { SettingsService } from '../../theme/pages/home/_services/settings.service';
import { TelephonyService } from '../../theme/pages/home/telephony/telephony.service';

@Injectable({
    providedIn: 'root',
})
export class EambulantaUserInitService extends UserInitializationService {
    constructor() {
        super();
    }

    private store = inject(Store);
    private storage = inject(StorageService);
    private configGUIRest = inject(ConfigGuiService);
    private commonRest = inject(CommonService);
    private helper = inject(HelperService);
    private rules = inject(RulesService);
    private toast = inject(MfToastService);
    private contractorSettingsApplicationService = inject(ContractorSettingsApplicationService);
    private settingsRest = inject(SettingsService);
    private activeModules = inject(ActiveModulesService);
    private telephony = inject(TelephonyService);
    private helperNotification = inject(HelperNotificationService);
    private menuService = inject(MenuService);

    override initializeUserData(): Observable<unknown> {
        const version = this.helper.getAppVersion();
        return forkJoin({
            userSettings: this.configGUIRest.getUserGuiSettings(),
            userData: this.commonRest.getUserData(),
        }).pipe(
            tap(() => {
                this.storage.deleteAll();
                this.storage.deleteUserData();
                this.storage.setAppData({ version });
            }),
            map((res: { userSettings: GuiUserSettings; userData: User }) => {
                let selectedContractorGroup: ContractorGroup = undefined;
                if (!_.isNil(res.userSettings)) {
                    this.configGUIRest.updateUserGuiSettings(res.userSettings);
                    selectedContractorGroup = res.userData.userContractors.find(el => el.contractorId === res.userSettings.lastSelectedContractorId);
                    if (_.isNil(selectedContractorGroup) && _.get(res, 'userData.userContractors.length') > 0) {
                        selectedContractorGroup = res.userData.userContractors[0];
                    }
                } else {
                    selectedContractorGroup = res.userData.userContractors.find(el => el.contractorId === res.userData.contractorId);
                }
                _.assign(
                    res.userData,
                    { employeeId: selectedContractorGroup.employeeId },
                    { subcontractorId: selectedContractorGroup.subcontractorId },
                );
                const loggedUser: User = new User().deserialize(res.userData);
                this.storage.setUserData(loggedUser);
                const selectedContractor: Contractor = res.userData.contractors.find(el => el.id === selectedContractorGroup.contractorId);
                this.storage.setSelectedContractor(selectedContractor);
                this.store.dispatch([new SetActiveContractor(selectedContractor), new SetLoggedUser(loggedUser)]);
                return selectedContractorGroup.contractorId;
            }),
            mergeMap(contractorId =>
                forkJoin([
                    this.commonRest.getUserRoles(contractorId).pipe(
                        tap((roles: string[]) => {
                            if (roles.length < 1) {
                                throw new Error('Nimate pravice za prijavo!');
                            }
                            this.rules.setRules();
                        }),
                        catchError(error => {
                            _.set(error, 'error.error_description', 'ROLES_ERROR');
                            this.toast.error(error.message);
                            return throwError(() => error);
                        }),
                    ),
                    this.configGUIRest.getUserContractorGuiSettings(contractorId).pipe(
                        tap(userSettings => {
                            const settings = new GuiUserContractorSettings().deserialize(userSettings);
                            if (!settings?.calendar?.filter) {
                                settings.calendar = this.helper.getDefaultCalendarFilterSettings();
                            }
                            this.storage.setGuiUserContractorSettings(settings);
                            this.store.dispatch(new SetUserSettings(settings));
                        }),
                    ),
                    this.contractorSettingsApplicationService.getContractorSettings(contractorId),
                    this.settingsRest.getContractorModules(contractorId).pipe(
                        tap(modules => {
                            this.store.dispatch([new SetActiveModules(modules)]);
                        }),
                    ),
                    this.settingsRest.getContractorSettings(contractorId).pipe(
                        tap(contractorSettings => {
                            if (contractorSettings) {
                                this.storage.setSettings(contractorSettings);
                            }
                        }),
                    ),
                ]),
            ),
            tap(() => {
                if (this.activeModules.isAM(AppModulesType.INBOX)) {
                    this.store.dispatch(new WebsocketActions.InitWebSocket());
                }
                this.telephony.init();
            }),
        );
    }

    override clearUserData(): Observable<unknown> {
        this.storage.deleteAll();
        this.storage.deleteUserData();
        this.storage.deleteAppData();
        this.storage.deleteContractorSettings();
        this.storage.deleteAvailibilityData();
        this.storage.deleteActiveModules();
        this.storage.deleteSelectedTerms();
        this.helperNotification.setNotificationFavicon(false);
        this.menuService.resetMenu();
        return forkJoin([
            this.store.dispatch(new ClearState()),
            this.store.dispatch(new ClearTreatmentState()),
            this.store.dispatch(new WebsocketActions.CloseWebSocket()),
        ]);
    }
}
